// Persist where the user was before launching an OAuth flow, so we can
// return them to the exact same preview state after sign-in (e.g. the
// guest sheet at /sheet/guest with its sessionStorage payload intact).
//
// We use localStorage (not sessionStorage) because some OAuth providers
// open a popup or redirect through an intermediate domain that may break
// the sessionStorage chain on certain browsers. localStorage is safe
// across the OAuth roundtrip and we always consume + clear it on return.

const KEY = "atlas_post_oauth_redirect";
const MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes

interface StoredRedirect {
  path: string; // pathname + search + hash
  ts: number;
  source?: string;
}

/**
 * Save the current URL (pathname + search + hash) so we can return here
 * after OAuth completes. Pass an optional `source` for analytics.
 * Skips persistence on the auth page itself (we never want to loop back
 * to /sign-in after a successful sign-in).
 */
export function setPostOAuthRedirect(source?: string): void {
  try {
    if (typeof window === "undefined") return;
    const { pathname, search, hash } = window.location;
    if (pathname.startsWith("/sign-in")) return;
    const path = `${pathname}${search}${hash}`;
    const payload: StoredRedirect = { path, ts: Date.now() };
    if (source !== undefined) payload.source = source;
    localStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    /* storage unavailable, ignore */
  }
}

/**
 * Read and clear the stored redirect. Returns null if nothing was stored
 * or if the entry has expired.
 */
export function consumePostOAuthRedirect(): StoredRedirect | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    localStorage.removeItem(KEY);
    const parsed = JSON.parse(raw) as StoredRedirect;
    if (!parsed?.path) return null;
    if (Date.now() - parsed.ts > MAX_AGE_MS) return null;
    // Defensive: only allow same-origin internal paths
    if (!parsed.path.startsWith("/")) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearPostOAuthRedirect(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}